import { apiErrorCode, apiFetch, v1Path } from './api';
import type { StateDto, WorkItemFull } from './work-types';

function itemPath(projectId: string, workItemId: string): string {
  return v1Path(`/projects/${projectId}/work-items/${workItemId}`);
}

/**
 * Saves fields on a task without the caller tracking its version: reads the latest version first,
 * and tries once more if someone else saved in between.
 */
export async function patchTask(projectId: string, workItemId: string, fields: Record<string, unknown>): Promise<WorkItemFull> {
  for (let attempt = 0; ; attempt += 1) {
    const fresh = await apiFetch<WorkItemFull>(itemPath(projectId, workItemId));
    try {
      return await apiFetch<WorkItemFull>(itemPath(projectId, workItemId), {
        method: 'PATCH',
        body: JSON.stringify({ version: fresh.version, ...fields }),
      });
    } catch (err) {
      if (attempt === 0 && apiErrorCode(err) === 'STALE_VERSION') continue;
      throw err;
    }
  }
}

/** The state to use for done, and the one to go back to when reopened. */
export function doneStates(states: StateDto[]): { done: StateDto | null; open: StateDto | null } {
  const done = states.find((state) => state.group === 'completed') ?? null;
  const open =
    states.find((state) => state.is_default && state.group !== 'completed') ??
    states.find((state) => state.group === 'unstarted' || state.group === 'backlog') ??
    null;
  return { done, open };
}

/**
 * The statuses a business requirement offers: not started, in progress, done. (Blocked is added by the menu.)
 * Planned and Cancelled are left out, but a requirement already sitting in one keeps showing it.
 */
export function requirementStatusOptions(states: StateDto[], current: StateDto | undefined): StateDto[] {
  const wanted = new Set(['backlog', 'started', 'completed']);
  const seen = new Set<string>();
  return states.filter((state) => {
    const keep = state.id === current?.id || (wanted.has(state.group) && !seen.has(state.group));
    if (keep && wanted.has(state.group)) seen.add(state.group);
    return keep;
  });
}

/** How a requirement names its status. The workspace's Backlog reads as "To Do" here; the stored state is unchanged. */
export function requirementStatusLabel(state: Pick<StateDto, 'name' | 'group'> | undefined): string {
  if (!state) return 'No status';
  return state.group === 'backlog' ? 'To Do' : state.name;
}

/** A pasted web address as an http(s) URL, or null when it is not one. `example.com` gets https. */
export function normalizeLink(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed || /\s/.test(trimmed)) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (!url.hostname.includes('.') && url.hostname !== 'localhost') return null;
    return url.toString();
  } catch {
    return null;
  }
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** A paragraph holding one link, for adding to a task's notes. */
export function linkParagraph(url: string, label: string): string {
  const text = label.trim() || url;
  return `<p><a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(text)}</a></p>`;
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
